import { colors } from "@/theme/colors"

/**
 * The motion behind the screens that draw themselves.
 *
 * Ported from `Report generation & Break screens explorations.dc`, which is the
 * source of truth for every constant here. The numbers are not tuneable in any
 * meaningful sense — they are the approved timings, and changing one changes a
 * screen the room watches from across a table.
 *
 * Everything is a pure function of a single progress value `p` in 0…1. No
 * component here owns a clock: `useAssembly` produces `p` and re-renders, and
 * these turn it into per-glyph styles. That split is why one animation can be
 * replayed, scrubbed, or skipped outright under `prefers-reduced-motion` by
 * simply handing it `p = 1`.
 */

/** One character of an assembling drawing, ready to be spread onto a span. */
export interface Glyph {
  ch: string
  opacity: number
  transform: string
  color: string
}

export interface GlyphRow {
  chars: Glyph[]
}

/** Where in the 0…1 arc a band of glyphs starts, and how tall that band is. */
export interface AssembleBox {
  /** The progress at which the top row begins to land. */
  top: number
  /** How much of the arc the whole drawing takes to land. */
  height: number
}

const MONO = colors.motion
const INK_SETTLED = MONO.settled
/** A finished tally line, and one still resolving. */
const WRITTEN = "rgb(23 20 15 / 74%)"
const WRITING = "rgb(23 20 15 / 50%)"

/*
 * A hash, not a random: the same glyph must scatter to the same place on every
 * frame and on every replay, or the drawing shimmers instead of assembling.
 * Seeding from the character's index is what makes the motion deterministic
 * without storing a particle per glyph.
 */
function noise(i: number): number {
  const x = Math.sin(i * 127.1 + 11.7) * 43758.5453
  return x - Math.floor(x)
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

/**
 * Overshoot easing. A glyph flying into place passes its mark and comes back —
 * without it the assembly reads as a fade, which is the thing it is not.
 */
export function overshoot(e: number): number {
  const c = 1.9
  const t = e - 1
  return e >= 1 ? 1 : 1 + (c + 1) * t * t * t + c * t * t
}

/**
 * The local 0…1 for something that starts at `at` and takes `window` of the
 * overall arc. This is how one clock drives twenty staggered elements.
 */
export function reveal(p: number, at: number, window: number): number {
  return window > 0 ? clamp01((p - at) / window) : p >= at ? 1 : 0
}

/** The characters a still-scrambling line is drawn from. */
const POOL = "─│╱╲◇◦●✕~+<>≡"
/** How many characters of noise run ahead of the decoded text. */
const NOISE_RUN = 6
/** The scramble reshuffles on its own clock, slower than the frame rate. */
const SCRAMBLE_MS = 70

/**
 * A line of text resolving out of noise, left to right.
 *
 * `e` is the line's own progress. Below 1 the text is cut at `e` and followed by
 * a short run of garbage, so a label reads as being decoded rather than typed —
 * the room sees the shape of the finished word before it can read it.
 */
export function decode(text: string, e: number, seed: number): string {
  if (e <= 0) return text.replace(/\S/g, " ")
  if (e >= 1) return text

  const frame = Math.floor(performance.now() / SCRAMBLE_MS)
  const cut = Math.floor(text.length * e)
  let out = text.slice(0, cut)
  for (let j = cut; j < Math.min(text.length, cut + NOISE_RUN); j++) {
    out += text[j] === " " ? " " : POOL[Math.floor(noise(seed * 97 + j * 7 + frame) * POOL.length)]
  }
  /* Padded, never short: a line that changes width drags the ones beside it. */
  while (out.length < text.length) out += " "
  return out
}

/**
 * A glyph is tinted toward a hue while it is in flight and settles to ink.
 * The mix is the whole reason the assembly has colour at all — nothing here is
 * ever painted at full saturation once it has landed.
 */
function tinted(hue: string, settle: number, floor = 0): string {
  const strength = Math.round(floor + (100 - floor) * (1 - settle))
  return `color-mix(in srgb, ${hue} ${strength}%, ${INK_SETTLED})`
}

/** Which corner of the drawing a filled glyph flies in on. */
function quadrant(row: number, rows: number, col: number, cols: number): string {
  const top = row < rows / 2
  const left = col < cols / 2
  if (top) return left ? MONO.scan.topLeft : MONO.scan.topRight
  return left ? MONO.scan.bottomLeft : MONO.scan.bottomRight
}

/**
 * The last moment a glyph may still be moving.
 *
 * Everything has to be at rest before the arc ends. The stagger is built from a
 * per-glyph jitter and a landing window that are both scaled off the height of
 * ONE row, so a drawing with few rows has enormous ones — a three-row motif
 * schedules its most-jittered characters to finish at p = 1.15, and since the
 * clock stops at 1 they stay frozen off their mark for as long as the screen is
 * up. `fitted` below squeezes the schedule when that would happen, which is a
 * no-op for the taller drawings.
 */
const LANDED_BY = 0.98

/**
 * How much to compress a schedule so its slowest glyph still lands. 1 whenever
 * the drawing already finishes in time, which is the usual case.
 *
 * `window` is the LONGEST of a glyph's windows, not the landing one. A glyph
 * has two — it flies into place over `landing` and cools to ink over
 * `settling`, which is roughly twice as long — and squeezing against the
 * shorter of them fits the position and leaves the colour hanging. On the
 * three-row `hand` motif that the deep dive, the job gate and the wrap curtain
 * all draw, thirteen of the twenty tinted glyphs reached p = 1 still between
 * 78% and 86% of the way to ink against their neighbours' 74%, and stayed
 * there for as long as the screen was up: the bottom row of card frames read
 * permanently redder than the top one.
 */
function fitted(top: number, height: number, rows: number, jitter: number, window: number): number {
  const latest = (height * (rows - 1)) / rows + jitter + window
  if (latest <= 0) return 1
  return Math.min(1, (LANDED_BY - top) / latest)
}

interface AssembleOptions extends AssembleBox {
  /** How far the glyphs scatter before they come in. */
  spread?: number
  /**
   * Colour by quadrant rather than by ink. On for the curtains' motifs, where
   * the drawing is the only colour on the screen; off would be a grey assembly.
   */
  hues?: boolean
}

/**
 * Turn an ASCII drawing into per-glyph styles at progress `p`.
 *
 * Rows land top to bottom with a per-glyph jitter, so the drawing resolves as a
 * wash rather than as a wipe. `0` and `=` are the filled characters (a card's
 * face), `+` and `*` the accents, everything else scaffolding — that is the
 * convention `content/ascii.ts` already draws to.
 */
export function assemble(
  p: number,
  lines: readonly string[],
  options: AssembleOptions
): GlyphRow[] {
  const { top, height, spread = 1, hues = false } = options
  const rows = lines.length
  const band = height / rows
  const jitterMax = band * 3.2
  const landing = Math.max(band * 3.4, 0.03)
  const settling = Math.max(band * 6.5, 0.06)
  const fit = fitted(top, height, rows, jitterMax, Math.max(landing, settling))
  let index = 0

  return lines.map((text, row) => {
    const rowTop = (row / rows) * height
    const chars: Glyph[] = []

    for (let col = 0; col < text.length; col++) {
      const ch = text[col]
      const k = index++

      if (ch === " ") {
        chars.push({ ch: " ", opacity: 0, transform: "none", color: "transparent" })
        continue
      }

      const filled = ch === "0" || ch === "="
      const accent = ch === "+" || ch === "*"
      const start = top + (rowTop + noise(k + 11) * jitterMax) * fit
      const landed = reveal(p, start, landing * fit)
      const e = overshoot(landed)
      /* The colour settles more slowly than the position, so a glyph is already
         in place while it is still cooling to ink. */
      const settle = reveal(p, start, settling * fit)

      let hue: string
      if (hues) {
        if (filled) hue = quadrant(row, rows, col, text.length)
        else if (accent) hue = MONO.scan.middle
        else hue = MONO.scan.frame
      } else {
        hue = filled ? colors.ink[900] : accent ? MONO.mark.warm : colors.ink[400]
      }

      const dx = (noise(k + 1) * 2 - 1) * 74 * spread * (1 - e)
      const dy = (noise(k + 2) * 2 - 1) * 56 * spread * (1 - e)
      const rot = (noise(k + 3) * 2 - 1) * 52 * (1 - e)

      chars.push({
        ch,
        opacity: landed,
        transform: `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) rotate(${rot.toFixed(1)}deg) scale(${(0.55 + 0.45 * e).toFixed(3)})`,
        /* Scaffolding is never tinted: it is the part of the drawing that is
           only there to hold the shape. */
        color: filled || accent ? tinted(hue, settle, hues ? 74 : 0) : hue,
      })
    }

    return { chars }
  })
}

/** One plate of a multi-coloured drawing: its rows and the hue they fly in on. */
export interface MarkPlate {
  lines: readonly string[]
  hue: string
}

/**
 * The report mark. Same assembly as `assemble`, but every glyph takes the hue of
 * the plate it belongs to rather than of the quadrant it sits in — the three
 * plates are three separate shapes stacked, and colouring them by position would
 * cut each one in half.
 */
export function assembleMark(
  p: number,
  plates: readonly MarkPlate[],
  box: AssembleBox
): GlyphRow[] {
  const flat = plates.flatMap((plate) => plate.lines.map((text) => ({ text, hue: plate.hue })))
  const rows = flat.length
  const band = box.height / rows
  const jitterMax = band * 3.4
  const landing = Math.max(band * 3.6, 0.03)
  const settling = Math.max(band * 7, 0.06)
  const fit = fitted(box.top, box.height, rows, jitterMax, Math.max(landing, settling))
  let index = 0

  return flat.map((line, row) => {
    const rowTop = (row / rows) * box.height
    const chars: Glyph[] = []

    for (let col = 0; col < line.text.length; col++) {
      const ch = line.text[col]
      const k = index++

      if (ch === " ") {
        chars.push({ ch: " ", opacity: 0, transform: "none", color: "transparent" })
        continue
      }

      const start = box.top + (rowTop + noise(k + 11) * jitterMax) * fit
      const landed = reveal(p, start, landing * fit)
      const e = overshoot(landed)
      const settle = reveal(p, start, settling * fit)
      const dx = (noise(k + 1) * 2 - 1) * 130 * (1 - e)
      const dy = (noise(k + 2) * 2 - 1) * 95 * (1 - e)
      const rot = (noise(k + 3) * 2 - 1) * 64 * (1 - e)

      chars.push({
        ch,
        opacity: landed,
        transform: `translate(${dx.toFixed(1)}px, ${dy.toFixed(1)}px) rotate(${rot.toFixed(1)}deg) scale(${(0.5 + 0.5 * e).toFixed(3)})`,
        color: tinted(line.hue, settle),
      })
    }

    return { chars }
  })
}

/** A line of the report's tally, mid-decode. */
export interface WrittenLine {
  text: string
  visible: boolean
  color: string
}

/**
 * The tally beside the mark, written one line at a time.
 *
 * The last line is the accent, because it is the only one that is an outcome
 * rather than a count — it is what the whole screen is waiting to say.
 */
export function writeLines(p: number, targets: readonly string[], box: AssembleBox): WrittenLine[] {
  const n = targets.length
  const window = Math.max((box.height / n) * 1.6, 0.035)
  /* The last line has to finish inside the arc, not at the end of it: a tally
     whose final row is still scrambling when the screen changes reads as a
     failure. */
  const lastStart = Math.min(box.top + box.height, 0.94 - window)
  const step = n > 1 ? Math.max(0, (lastStart - box.top) / (n - 1)) : 0

  return targets.map((text, i) => {
    const e = reveal(p, box.top + i * step, window)
    return {
      text: decode(text, e, i),
      visible: e > 0,
      /* A line is faint while it scrambles and firms up once it resolves, so
         the eye follows the write down the list without trying to read ahead. */
      color: e >= 1 ? (i === n - 1 ? MONO.accent : WRITTEN) : WRITING,
    }
  })
}
